import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { NEWSLETTER_CAMPAIGNS_COLLECTION } from '../utils/firestoreCollections';
import { normalizeNewsletterCampaign } from '../utils/normalizeNewsletterCampaign';

export { normalizeNewsletterCampaign };

export function useNewsletterCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const campaignsQuery = query(
        collection(db, NEWSLETTER_CAMPAIGNS_COLLECTION),
        orderBy('createdAt', 'desc'),
      );
      const snapshot = await getDocs(campaignsQuery);
      setCampaigns(
        snapshot.docs.map((docSnap) =>
          normalizeNewsletterCampaign(docSnap.id, docSnap.data()),
        ),
      );
    } catch (fetchError) {
      setError(
        fetchError instanceof Error
          ? fetchError
          : new Error('Unable to load newsletter campaigns'),
      );
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { campaigns, loading, error, refresh };
}

export function useNewsletterCampaign(campaignId) {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadCampaign() {
      if (typeof campaignId !== 'string' || campaignId.length === 0) {
        setCampaign(null);
        setLoading(false);
        setError(new Error('Missing campaign id'));
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const snapshot = await getDoc(
          doc(db, NEWSLETTER_CAMPAIGNS_COLLECTION, campaignId),
        );
        if (cancelled) {
          return;
        }
        if (!snapshot.exists()) {
          setCampaign(null);
          setError(new Error('Campaign not found'));
          return;
        }
        setCampaign(normalizeNewsletterCampaign(snapshot.id, snapshot.data()));
      } catch (fetchError) {
        if (!cancelled) {
          setCampaign(null);
          setError(
            fetchError instanceof Error
              ? fetchError
              : new Error('Unable to load campaign'),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCampaign();
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  return { campaign, loading, error };
}
